import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  MapPin, Star, Clock, Phone, MessageCircle, Award, ArrowRight,
  Instagram, Facebook, Calendar, Shield, Send, Eye, Pencil, X, Save, Users, Trash2
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const CoachProfilePage = () => {
  const { coachId } = useParams();
  const { API, user } = useContext(AuthContext);
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [showViewers, setShowViewers] = useState(false);
  const [loadingViewers, setLoadingViewers] = useState(false);

  useEffect(() => {
    fetchCoach();
  }, [coachId]);

  const fetchCoach = async () => {
    try {
      const response = await axios.get(`${API}/coaches/${coachId}`);
      setCoach(response.data);
    } catch (error) {
      console.error('Failed to fetch coach:', error);
      toast.error('לא ניתן לטעון את פרטי המאמן');
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user?.email);
  const isOwner = user && coach && user.id === coach.user_id;
  const canEdit = isAdmin || isOwner;

  const startEdit = () => {
    setEditData({
      full_name: coach.full_name || '',
      phone: coach.phone || '',
      email: coach.email || '',
      city: coach.city || '',
      area: coach.area || '',
      club: coach.club || '',
      specialties: coach.specialties || [],
      experience_years: coach.experience_years || 0,
      price_private: coach.price_private || '',
      price_duo: coach.price_duo || '',
      price_group: coach.price_group || '',
      description: coach.description || '',
      certifications: coach.certifications || '',
      availability: coach.availability || '',
      whatsapp: coach.whatsapp || '',
      instagram: coach.instagram || '',
      facebook: coach.facebook || '',
    });
    setEditMode(true);
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const toggleEditSpecialty = (spec) => {
    setEditData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(spec)
        ? prev.specialties.filter(s => s !== spec)
        : [...prev.specialties, spec]
    }));
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/coaches/${coachId}`, editData);
      toast.success('הפרופיל עודכן בהצלחה!');
      setEditMode(false);
      fetchCoach();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בעדכון הפרופיל');
    } finally {
      setSaving(false);
    }
  };

  const fetchViewers = async () => {
    setLoadingViewers(true);
    try {
      const response = await axios.get(`${API}/admin/coaches/${coachId}/viewers`);
      setViewers(response.data);
      setShowViewers(true);
    } catch (error) {
      toast.error('שגיאה בטעינת רשימת הצופים');
    } finally {
      setLoadingViewers(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!user) {
      toast.error('עליך להיות מחובר כדי לכתוב ביקורת');
      return;
    }
    if (reviewRating === 0) {
      toast.error('אנא בחר דירוג (כוכבים)');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/coaches/${coachId}/review`, {
        rating: reviewRating,
        comment: reviewComment,
      });
      toast.success('הביקורת נוספה בהצלחה!');
      setReviewRating(0);
      setReviewComment('');
      fetchCoach();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בשליחת הביקורת');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('למחוק את הביקורת הזו?')) return;
    try {
      await axios.delete(`${API}/admin/coaches/${coachId}/reviews/${reviewId}`);
      toast.success('הביקורת נמחקה');
      fetchCoach();
    } catch (error) {
      toast.error('שגיאה במחיקת הביקורת');
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    return parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : name.substring(0, 2);
  };

  const renderStars = (rating, size = 'w-5 h-5') => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`${size} ${i < Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  const formatWhatsApp = (number) => {
    if (!number) return '';
    let clean = number.replace(/[^0-9+]/g, '');
    if (clean.startsWith('+972')) clean = '972' + clean.slice(4);
    else if (clean.startsWith('972')) clean = clean;
    else if (clean.startsWith('0')) clean = '972' + clean.slice(1);
    else clean = '972' + clean;
    return clean;
  };

  const getWhatsAppNumber = (coach) => {
    if (coach.whatsapp) return formatWhatsApp(coach.whatsapp);
    if (coach.phone) return formatWhatsApp(coach.phone);
    return '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-64 bg-gray-200 rounded-3xl" />
            <div className="h-8 bg-gray-200 rounded w-1/3" />
            <div className="h-4 bg-gray-200 rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!coach) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h2 className="text-2xl font-bold text-gray-700">מאמן לא נמצא</h2>
          <Link to="/coaches">
            <Button className="mt-4">חזרה לרשימת המאמנים</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Link to="/coaches" className="inline-flex items-center gap-2 text-gray-500 hover:text-emerald-600 transition-colors mb-6 group">
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          <span className="font-medium">חזרה לרשימת המאמנים</span>
        </Link>

        {/* Hero Card */}
        <Card className="overflow-hidden border-2 border-gray-100 shadow-xl mb-8" data-testid="coach-profile-card">
          <div className="h-3 bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-500" />
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <Avatar className="h-32 w-32 ring-4 ring-emerald-200 shadow-xl">
                  <AvatarImage src={coach.image} alt={coach.full_name} />
                  <AvatarFallback className="bg-gradient-to-br from-emerald-400 to-teal-600 text-white text-4xl font-bold">
                    {getInitials(coach.full_name)}
                  </AvatarFallback>
                </Avatar>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="coach-name">
                  {coach.full_name}
                </h1>

                {/* View count - visible to coach owner and admin */}
                {user && (user.id === coach.user_id || ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user.email)) && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full mb-3" data-testid="view-count">
                    <Eye className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600 font-medium">{coach.view_count || 0} צפיות בפרופיל</span>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <div className="flex items-center gap-1 text-gray-600">
                    <MapPin className="w-5 h-5 text-emerald-500" />
                    <span>{coach.city} | {coach.area}</span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-600">
                    <Clock className="w-5 h-5 text-emerald-500" />
                    <span>{coach.experience_years} שנות ניסיון</span>
                  </div>
                  {coach.club && (
                    <div className="flex items-center gap-1 text-gray-600">
                      <Shield className="w-5 h-5 text-emerald-500" />
                      <span>מועדון: <strong>{coach.club}</strong></span>
                    </div>
                  )}
                </div>

                {/* Rating */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex">{renderStars(coach.average_rating)}</div>
                  <span className="text-lg font-bold text-gray-800">{coach.average_rating || 0}</span>
                  <span className="text-gray-500">({coach.total_reviews} ביקורות)</span>
                </div>

                {/* Specialties */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {(coach.specialties || []).map((spec, i) => (
                    <span
                      key={i}
                      className="px-4 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium border border-emerald-200"
                    >
                      {spec}
                    </span>
                  ))}
                </div>

                {/* Price */}
                {(coach.price_private || coach.price_duo || coach.price_group) && (
                  <div className="flex flex-wrap gap-3 mt-1">
                    {coach.price_private && (
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border-2 border-emerald-300 rounded-xl">
                        <span className="text-emerald-600 text-sm">פרטי:</span>
                        <span className="text-emerald-700 font-bold text-lg">{coach.price_private}</span>
                      </div>
                    )}
                    {coach.price_duo && (
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border-2 border-blue-300 rounded-xl">
                        <span className="text-blue-600 text-sm">זוגי:</span>
                        <span className="text-blue-700 font-bold text-lg">{coach.price_duo}</span>
                      </div>
                    )}
                    {coach.price_group && (
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border-2 border-amber-300 rounded-xl">
                        <span className="text-amber-600 text-sm">קבוצתי:</span>
                        <span className="text-amber-700 font-bold text-lg">{coach.price_group}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Contact Buttons */}
            <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-gray-100">
              {(coach.whatsapp || coach.phone) && (
                <a
                  href={`https://wa.me/${getWhatsAppNumber(coach)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-green-500 hover:bg-green-600 text-white font-bold px-6" data-testid="whatsapp-btn">
                    <MessageCircle className="w-5 h-5 ml-2" />
                    שלח הודעה בוואטסאפ
                  </Button>
                </a>
              )}
              {coach.phone && (
                <a href={`tel:${coach.phone}`}>
                  <Button variant="outline" className="border-2 border-emerald-400 text-emerald-700 hover:bg-emerald-50 font-bold px-6" data-testid="call-btn">
                    <Phone className="w-5 h-5 ml-2" />
                    התקשר
                  </Button>
                </a>
              )}
              {coach.instagram && (
                <a href={`https://instagram.com/${coach.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-2 border-pink-400 text-pink-600 hover:bg-pink-50">
                    <Instagram className="w-5 h-5 ml-1" />
                    Instagram
                  </Button>
                </a>
              )}
              {coach.facebook && (
                <a href={coach.facebook.startsWith('http') ? coach.facebook : `https://facebook.com/${coach.facebook}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-2 border-blue-400 text-blue-600 hover:bg-blue-50">
                    <Facebook className="w-5 h-5 ml-1" />
                    Facebook
                  </Button>
                </a>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Edit & Viewers Buttons */}
        {canEdit && (
          <div className="flex flex-wrap gap-3 mb-6" data-testid="coach-actions">
            {!editMode && (
              <Button
                onClick={startEdit}
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold px-6"
                data-testid="edit-coach-btn"
              >
                <Pencil className="w-4 h-4 ml-2" />
                ערוך פרופיל
              </Button>
            )}
            {isAdmin && (
              <Button
                onClick={fetchViewers}
                variant="outline"
                className="border-2 border-purple-400 text-purple-600 hover:bg-purple-50 font-bold"
                data-testid="view-viewers-btn"
                disabled={loadingViewers}
              >
                <Users className="w-4 h-4 ml-2" />
                {loadingViewers ? 'טוען...' : 'מי צפה בפרופיל'}
              </Button>
            )}
          </div>
        )}

        {/* Viewers Modal - Admin Only */}
        {showViewers && isAdmin && (
          <Card className="border-2 border-purple-200 shadow-lg mb-8" data-testid="viewers-list">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Eye className="w-6 h-6 text-purple-500" />
                  צפיות בפרופיל ({viewers.length})
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowViewers(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              {viewers.length === 0 ? (
                <p className="text-gray-500 text-center py-4">אין צפיות עדיין</p>
              ) : (
                <div className="max-h-80 overflow-y-auto space-y-2">
                  {viewers.map((v, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={v.viewer_avatar} />
                        <AvatarFallback className="bg-gradient-to-br from-purple-400 to-pink-500 text-white text-xs font-bold">
                          {v.viewer_name ? v.viewer_name.substring(0, 2) : '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 text-sm truncate">{v.viewer_name || 'אנונימי'}</p>
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {new Date(v.viewed_at).toLocaleDateString('he-IL')} {new Date(v.viewed_at).toLocaleTimeString('he-IL', {hour: '2-digit', minute: '2-digit'})}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Edit Form */}
        {editMode && (
          <Card className="border-2 border-blue-200 shadow-lg mb-8" data-testid="edit-coach-form">
            <div className="h-2 bg-gradient-to-r from-blue-400 to-cyan-500" />
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Pencil className="w-5 h-5 text-blue-500" />
                  עריכת פרופיל מאמן
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setEditMode(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-bold text-gray-700">שם מלא</Label>
                    <Input value={editData.full_name} onChange={e => handleEditChange('full_name', e.target.value)} className="mt-1" data-testid="edit-full-name" />
                  </div>
                  <div>
                    <Label className="text-sm font-bold text-gray-700">טלפון</Label>
                    <Input value={editData.phone} onChange={e => handleEditChange('phone', e.target.value)} className="mt-1" data-testid="edit-phone" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-bold text-gray-700">עיר</Label>
                    <Input value={editData.city} onChange={e => handleEditChange('city', e.target.value)} className="mt-1" data-testid="edit-city" />
                  </div>
                  <div>
                    <Label className="text-sm font-bold text-gray-700">אזור</Label>
                    <select value={editData.area} onChange={e => handleEditChange('area', e.target.value)} className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="edit-area">
                      <option value="">בחר</option>
                      {['צפון','שרון','מרכז','ירושלים','דרום'].map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-bold text-gray-700">מועדון</Label>
                  <Input value={editData.club} onChange={e => handleEditChange('club', e.target.value)} className="mt-1" data-testid="edit-club" />
                </div>
                <div>
                  <Label className="text-sm font-bold text-gray-700 mb-2 block">התמחויות</Label>
                  <div className="flex flex-wrap gap-2">
                    {['ילדים','נוער','מתחילים','מתקדמים','מבוגרים','תחרותי'].map(spec => (
                      <button key={spec} type="button" onClick={() => toggleEditSpecialty(spec)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${editData.specialties?.includes(spec) ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
                        {spec}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">מחיר פרטי</Label>
                    <Input value={editData.price_private} onChange={e => handleEditChange('price_private', e.target.value)} className="mt-1" data-testid="edit-price-private" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">מחיר זוגי</Label>
                    <Input value={editData.price_duo} onChange={e => handleEditChange('price_duo', e.target.value)} className="mt-1" data-testid="edit-price-duo" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">מחיר קבוצתי</Label>
                    <Input value={editData.price_group} onChange={e => handleEditChange('price_group', e.target.value)} className="mt-1" data-testid="edit-price-group" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-bold text-gray-700">שנות ניסיון</Label>
                  <Input type="number" min="0" value={editData.experience_years} onChange={e => handleEditChange('experience_years', parseInt(e.target.value) || 0)} className="mt-1 max-w-[120px]" data-testid="edit-experience" />
                </div>
                <div>
                  <Label className="text-sm font-bold text-gray-700">תיאור</Label>
                  <textarea value={editData.description} onChange={e => handleEditChange('description', e.target.value)} className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm min-h-[100px] focus:border-blue-400 focus:outline-none" data-testid="edit-description" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-bold text-gray-700">הסמכות</Label>
                    <Input value={editData.certifications} onChange={e => handleEditChange('certifications', e.target.value)} className="mt-1" data-testid="edit-certifications" />
                  </div>
                  <div>
                    <Label className="text-sm font-bold text-gray-700">זמינות</Label>
                    <Input value={editData.availability} onChange={e => handleEditChange('availability', e.target.value)} className="mt-1" data-testid="edit-availability" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">WhatsApp</Label>
                    <Input value={editData.whatsapp} onChange={e => handleEditChange('whatsapp', e.target.value)} className="mt-1" data-testid="edit-whatsapp" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Instagram</Label>
                    <Input value={editData.instagram} onChange={e => handleEditChange('instagram', e.target.value)} className="mt-1" data-testid="edit-instagram" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Facebook</Label>
                    <Input value={editData.facebook} onChange={e => handleEditChange('facebook', e.target.value)} className="mt-1" data-testid="edit-facebook" />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button onClick={handleSaveEdit} disabled={saving} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold px-8" data-testid="save-edit-btn">
                    <Save className="w-4 h-4 ml-2" />
                    {saving ? 'שומר...' : 'שמור שינויים'}
                  </Button>
                  <Button variant="outline" onClick={() => setEditMode(false)} className="border-gray-300">
                    ביטול
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Description & Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* About */}
          <Card className="border-2 border-gray-100 shadow-md">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Award className="w-6 h-6 text-emerald-500" />
                אודות המאמן
              </h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {coach.description || 'אין תיאור'}
              </p>
            </CardContent>
          </Card>

          {/* Details */}
          <Card className="border-2 border-gray-100 shadow-md">
            <CardContent className="p-6 space-y-4">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6 text-emerald-500" />
                פרטים נוספים
              </h2>
              {coach.club && (
                <div>
                  <span className="text-sm font-bold text-gray-500">מועדון:</span>
                  <p className="text-gray-700 font-medium">{coach.club}</p>
                </div>
              )}
              {coach.certifications && (
                <div>
                  <span className="text-sm font-bold text-gray-500">הסמכות:</span>
                  <p className="text-gray-700">{coach.certifications}</p>
                </div>
              )}
              {coach.availability && (
                <div>
                  <span className="text-sm font-bold text-gray-500">זמינות:</span>
                  <p className="text-gray-700">{coach.availability}</p>
                </div>
              )}
              {coach.email && (
                <div>
                  <span className="text-sm font-bold text-gray-500">אימייל:</span>
                  <p className="text-gray-700">{coach.email}</p>
                </div>
              )}
              <div>
                <span className="text-sm font-bold text-gray-500">הצטרף באתר:</span>
                <p className="text-gray-700">
                  {new Date(coach.created_at).toLocaleDateString('he-IL')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Reviews Section */}
        <Card className="border-2 border-gray-100 shadow-md mb-8" data-testid="reviews-section">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
              ביקורות ({coach.reviews?.length || 0})
            </h2>

            {/* Add Review */}
            {user && (
              <div className="bg-gray-50 rounded-2xl p-5 mb-6 border border-gray-200" data-testid="add-review-form">
                <h3 className="font-bold text-gray-800 mb-3">כתוב ביקורת</h3>
                {/* Star Rating */}
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }, (_, i) => (
                    <button
                      key={i}
                      onMouseEnter={() => setHoverRating(i + 1)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setReviewRating(i + 1)}
                      data-testid={`star-${i + 1}`}
                      className="transition-transform hover:scale-125"
                    >
                      <Star
                        className={`w-8 h-8 ${
                          i < (hoverRating || reviewRating)
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300'
                        } transition-colors`}
                      />
                    </button>
                  ))}
                  {reviewRating > 0 && (
                    <span className="text-sm text-gray-500 mr-2">{reviewRating}/5</span>
                  )}
                </div>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="ספר על החוויה שלך עם המאמן..."
                  className="w-full border-2 border-gray-200 rounded-xl p-3 text-sm min-h-[80px] focus:border-emerald-400 focus:outline-none resize-none"
                  data-testid="review-comment"
                />
                <Button
                  onClick={handleSubmitReview}
                  disabled={submitting || reviewRating === 0}
                  className="mt-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold"
                  data-testid="submit-review-btn"
                >
                  <Send className="w-4 h-4 ml-2" />
                  {submitting ? 'שולח...' : 'שלח ביקורת'}
                </Button>
              </div>
            )}

            {/* Reviews List */}
            {coach.reviews && coach.reviews.length > 0 ? (
              <div className="space-y-4">
                {coach.reviews.map((review) => (
                  <div key={review.id} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm" data-testid={`review-${review.id}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={review.user_avatar} />
                        <AvatarFallback className="bg-gradient-to-br from-blue-400 to-cyan-500 text-white text-sm font-bold">
                          {getInitials(review.user_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-bold text-gray-800">{review.user_name}</p>
                        <div className="flex items-center gap-1">
                          {renderStars(review.rating, 'w-3.5 h-3.5')}
                          <span className="text-xs text-gray-400 mr-2">
                            {new Date(review.created_at).toLocaleDateString('he-IL')}
                          </span>
                        </div>
                      </div>
                      {isAdmin && (
                        <button
                          onClick={() => handleDeleteReview(review.id)}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="מחק ביקורת"
                          data-testid={`delete-review-${review.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {review.comment && (
                      <p className="text-gray-600 text-sm leading-relaxed">{review.comment}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-6">
                אין ביקורות עדיין. היה הראשון לכתוב ביקורת!
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CoachProfilePage;
